// src/components/mpi/D1ProductivityDrillDown.js
import { useEffect, useRef } from 'react';

function scoreColor(v) {
  if (v <= 40) return '#EF4444';
  if (v <= 65) return '#F59E0B';
  return '#10B981';
}

function MiniGauge({ score, size = 80 }) {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const dash = score != null ? circ * (score / 100) : 0;
  const gap = circ - dash;
  const col = score != null ? scoreColor(score) : '#475569';

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="rgba(255,255,255,0.07)" strokeWidth={6}
          style={{ transform: 'rotate(-90deg)', transformOrigin: `${size / 2}px ${size / 2}px` }}
        />
        {score != null && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={col} strokeWidth={6}
            strokeDasharray={`${dash} ${gap}`} strokeLinecap="round"
            style={{
              transform: 'rotate(-90deg)', transformOrigin: `${size / 2}px ${size / 2}px`,
              filter: `drop-shadow(0 0 8px ${col}66)`,
            }}
          />
        )}
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          fontSize: size > 100 ? 36 : 22, fontWeight: 800, lineHeight: 1, letterSpacing: '-1.5px',
          color: col,
          fontFamily: "'SF Pro Display', -apple-system, sans-serif",
        }}>
          {score != null ? score.toFixed(0) : '—'}
        </div>
      </div>
    </div>
  );
}

function TrendBadge({ value }) {
  if (value == null) return null;
  const col = value > 0 ? '#10B981' : value < 0 ? '#EF4444' : 'rgba(255,255,255,0.3)';
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '→';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20,
      background: `${col}14`, border: `1px solid ${col}40`,
      fontSize: 11, fontWeight: 600, color: col,
    }}>
      <span style={{ fontSize: 9 }}>{arrow}</span>
      {value > 0 ? '+' : ''}{value.toFixed(1)} ggü. Vorquartal
    </div>
  );
}

export default function D1ProductivityDrillDown({ current, previous, period, onClose }) {
  const boxRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleOverlayClick(e) {
    if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
  }

  const prod = current?.d1_productivity != null ? parseFloat(current.d1_productivity) : null;
  const qual = current?.d1_quality != null ? parseFloat(current.d1_quality) : null;
  const d1Total = (prod != null && qual != null) ? parseFloat((0.6 * prod + 0.4 * qual).toFixed(1)) : null;

  const prevProd = previous?.d1_productivity != null ? parseFloat(previous.d1_productivity) : null;
  const prevQual = previous?.d1_quality != null ? parseFloat(previous.d1_quality) : null;
  const prevD1Total = (prevProd != null && prevQual != null) ? parseFloat((0.6 * prevProd + 0.4 * prevQual).toFixed(1)) : null;

  const trendTotal = (d1Total != null && prevD1Total != null) ? parseFloat((d1Total - prevD1Total).toFixed(1)) : null;
  const trendProd = (prod != null && prevProd != null) ? parseFloat((prod - prevProd).toFixed(1)) : null;
  const trendQual = (qual != null && prevQual != null) ? parseFloat((qual - prevQual).toFixed(1)) : null;

  const hasData = d1Total != null;

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={boxRef}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 860,
          maxHeight: '85vh',
          background: 'linear-gradient(160deg, #111827 0%, #080b12 100%)',
          border: '1px solid rgba(100,116,139,0.2)',
          borderRadius: 20,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#64748B', boxShadow: '0 0 10px #64748B',
              flexShrink: 0, marginTop: 5,
            }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.3px' }}>
                D1 · Produktivität &amp; Qualität – Drill-Down
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                {period && <>{period} · </>}
                {hasData ? 'Gewichtung: 60% Produktivität + 40% Qualität' : 'Keine Daten verfügbar'}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer', padding: '6px 12px', fontSize: 11, flexShrink: 0,
            }}
          >
            ✕ Schließen
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {!hasData && (
            <div style={{
              padding: '40px 20px', textAlign: 'center',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 14,
            }}>
              <div style={{ fontSize: 28, color: '#475569', marginBottom: 12 }}>—</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
                Noch keine Produktivitäts- und Qualitätsdaten für diese Periode.
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 6 }}>
                Daten werden verfügbar sobald die CRM-Integration aktiv ist.
              </div>
            </div>
          )}

          {hasData && (
            <>
              {/* Block 1: Gesamt-Score */}
              <div style={{
                marginBottom: 24, padding: '24px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
                display: 'flex', alignItems: 'center', gap: 28,
              }}>
                <MiniGauge score={d1Total} size={120} />
                <div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>
                    D1 Gesamt-Score
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
                    <div style={{
                      fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                      color: scoreColor(d1Total),
                      fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                    }}>
                      {d1Total.toFixed(1)}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', paddingBottom: 6 }}></div>
                  </div>
                  <TrendBadge value={trendTotal} />
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
                    D1 = 60% Produktivität + 40% Qualität
                  </div>
                </div>
              </div>

              {/* Block 2: Subdimensionen */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24,
              }}>
                {/* Produktivität */}
                <div style={{
                  padding: '22px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Produktivität
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                        Sales-Aktivität
                      </div>
                    </div>
                    <MiniGauge score={prod} size={64} />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
                    <div style={{
                      fontSize: 36, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1,
                      color: scoreColor(prod),
                      fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                    }}>
                      {prod.toFixed(0)}
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', paddingBottom: 4 }}></div>
                  </div>

                  <TrendBadge value={trendProd} />

                  <div style={{
                    marginTop: 14, padding: '8px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 8, borderLeft: '2px solid #64748B',
                  }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
                      Funnel-Zielerreichung der Sales-Mitarbeiter
                    </div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
                      Gewicht: 60% von D1
                    </div>
                  </div>
                </div>

                {/* Qualität */}
                <div style={{
                  padding: '22px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Qualität
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                        Wirtschaftliches Ergebnis
                      </div>
                    </div>
                    <MiniGauge score={qual} size={64} />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
                    <div style={{
                      fontSize: 36, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1,
                      color: scoreColor(qual),
                      fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                    }}>
                      {qual.toFixed(0)}
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', paddingBottom: 4 }}></div>
                  </div>

                  <TrendBadge value={trendQual} />

                  <div style={{
                    marginTop: 14, padding: '8px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 8, borderLeft: '2px solid #64748B',
                  }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
                      Deckungsbeitrags-Planerreichung der NL
                    </div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
                      Gewicht: 40% von D1
                    </div>
                  </div>
                </div>
              </div>

              {/* Block 3: CRM-Hinweis */}
              <div style={{
                padding: '14px 18px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 12,
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'rgba(100,116,139,0.15)',
                  border: '1px solid rgba(100,116,139,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, color: '#64748B', flexShrink: 0,
                }}>
                  ℹ
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
                  Sales-Funnel-Details (Kontakte, Termine, Angebote, Einsätze) und BAB-Aufschlüsselung
                  (DB I/II/III) werden verfügbar sobald die CRM-Integration aktiv ist.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
